import { observable, action, reaction, computed } from 'mobx';
import { usersStore, translationsStore, profileStore } from '..';

import { api } from '../../services';

import moment from 'moment';

import initFormValues from './formDefault.json';

import * as R from 'ramda';

const parseMatriculationYear = raw => {
  if (raw == null) {
    return raw;
  }

  const str = String(raw).trim();
  if (!str || ['null', 'undefined', 'nan'].includes(str.toLowerCase())) {
    return undefined;
  }

  let parsed;
  if (/^\d{4}$/.test(str)) {
    parsed = moment(`${str}-01-01`, 'YYYY-MM-DD');
  } else if (/^\d{1,2}[/-]\d{4}$/.test(str)) {
    const [month, year] = str.split(/[/-]/);
    parsed = moment(`${year}-${month}-01`, 'YYYY-MM-DD');
  } else if (/^\d{4}[/-]\d{1,2}$/.test(str)) {
    const [year, month] = str.split(/[/-]/);
    parsed = moment(`${year}-${month}-01`, 'YYYY-MM-DD');
  } else {
    parsed = moment(str, ['YYYY-MM-DD', moment.ISO_8601]);
  }

  return parsed.isValid() ? parsed : undefined;
};

class RegistrationStore {
  @observable session; // user
  @observable lastSession;

  @observable user; //current user with asset (subscription (status and type), car, ..)
  @observable position = 0; //step position (always less or equal than status)
  @observable assetId;
  @observable formValues = initFormValues;

  @observable
  heartbeat = {
    status: 0
  };

  constructor(translationsStore, usersStore, profileStore) {
    this.translationsStore = translationsStore;
    this.usersStore = usersStore;
    this.profileStore = profileStore;
    reaction(
      () => this.session,
      session => {
        if (session) {
          let u = usersStore.pending.find(p => p._id === session);
          this.setUser(u);
        } else {
          this.setHeartbeat();
        }
      }
    );
  }

  @action
  reloadHeartbeat() {
    this.setHeartbeat();
    if (this.user) {
      api({ action: 'heartbeat' }, this.user._id).then(this.setHeartbeat.bind(this));
    }
  }

  @action
  setHeartbeat(heartbeat) {
    if (!heartbeat) {
      this.heartbeat = { status: 0 };
    } else {
      this.heartbeat = heartbeat && heartbeat.timestamp && heartbeat.batt_voltage ? { status: 1 } : { status: -1 };
    }
  }

  @action
  setStatus(status) {
    if (this.user) {
      this.user.assets[0].subscription.status = status;
    }
  }

  @computed
  get status() {
    return this.user ? this.user.assets[0].subscription.status : 0;
  }

  @computed
  get signatureMethod() {
    return this.user ? this.user.assets[0].subscription.signatureMethod : 'classic';
  }

  @computed
  get subscription() {
    if (this.user && this.user.assets && this.user.assets[0]) {
      return this.user ? this.user.assets[0].subscription : undefined;
    }
    return undefined;
  }

  @computed
  get car() {
    if (this.user && this.user.assets && this.user.assets[0]) {
      return this.user ? this.user.assets[0].car : undefined;
    }
    return undefined;
  }

  @computed
  get plate() {
    if (this.user && this.user.assets && this.user.assets[0] && this.user.assets[0].car) {
      return this.user ? this.user.assets[0].car.plate : undefined;
    }
    return undefined;
  }

  @computed
  get vehicleType() {
    if (this.user && this.user.assets && this.user.assets[0] && this.user.assets[0].car) {
      return this.user ? this.user.assets[0].car.vehicleType : undefined;
    }
    return undefined;
  }

  @computed get maintenances() {
    try {
      return this.user.assets[0].maintenances;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  @action
  setCustomMaintenances(customMaintenances) {
    let _user = Object.assign({}, this.user);
    let _maintenances = Object.assign({}, _user.assets[0].maintenances);
    _maintenances['custom'] = customMaintenances;
    _user.assets[0].maintenances = _maintenances;
    this.user = _user;
  }

  isNewSession(session) {
    return !this.lastSession || session !== this.lastSession;
  }

  @action
  setUserAsset(asset) {
    let _user = Object.assign({}, this.user);
    _user.assets[0] = asset;
    this.user = _user;
  }

  @action
  setSession(session) {
    let u = session ? this.usersStore.pending.find(p => p._id === session) : false;
    //qui devo capire se caricare nuovi dati oppure no.
    //se questa sessione è la stessa di quella precedente, allora non faccio nulla, cioè tengo valorizzati eventuali modifiche precedenti

    if (this.isNewSession(session)) {
      this.session = session;
      this.lastSession = session;
      if (u) {
        this.loadData(u);
      }
    }
    if (u) {
      this.setUser(u);
    }
  }

  @action
  setUser(user) {
    this.user = user;
  }

  @action
  setPosition(position = 0) {
    this.position = position;
  }

  @action
  reset() {
    this.setStatus();
    this.setPosition();
    this.setFormField();
  }

  @action
  setFormField(step, field, value) {
    if (step && field) {
      this.formValues = R.assocPath([...`${step}.${field}`.split('.')], value, this.formValues);
    } else if (step) {
      this.formValues[step] = initFormValues[step];
    } else {
      this.formValues = initFormValues;
    }
  }

  @action
  loadData(u) {
    this.setFormField();
    this.loadStep1(u);
    this.assetId = u.assets[0]._id;
    this.formValues.step_0.imei = u.assets[0].imei;
    this.formValues.step_0.plate = u.assets[0].car.plate;
  }

  loadStep1(u) {
    this.formValues.step_1.user = {};
    this.formValues.step_1.user = Object.assign({}, this.formValues.step_1.user, {
      firstname: u.firstname,
      lastname: u.lastname,
      ragsoc: u.ragsoc,
      birthday: u.birthday ? moment(u.birthday, 'YYYY-MM-DDTHH:mm:ss.SSSZ') : u.birthday,
      address: u.address,
      number: u.number,
      city: u.city,
      postcode: u.postcode == null ? u.postcode : u.postcode + '',
      prov: u.prov,
      country: u.country || 'IT',
      taxcode: u.taxcode,
      email: u.email,
      personalcode: u.personalcode,
      mobile: u.mobile,
      secondaryMobile: u.secondaryMobile
    });

    if (u.assets[0].car) {
      if (u.assets[0].car.make) {
        this.setFormField('step_1', 'car.make.codiceMarca', u.assets[0].car.make.codiceMarca);
      } else {
        this.setFormField('step_1', 'car.make.codiceMarca');
      }

      if (u.assets[0].car.model) {
        this.setFormField('step_1', 'car.model.codiceModello', u.assets[0].car.model.codiceModello);
      } else {
        this.setFormField('step_1', 'car.model.codiceModello');
      }

      if (u.assets[0].car.type) {
        this.setFormField('step_1', 'car.type.codiceInfocarAM', u.assets[0].car.type.codiceInfocarAM);
      } else {
        this.setFormField('step_1', 'car.type.codiceInfocarAM');
      }

      this.setFormField('step_1', 'car.color', u.assets[0].car.color);
      this.setFormField('step_1', 'car.alimentation', u.assets[0].car.alimentation);
      this.setFormField('step_1', 'car.displacement', u.assets[0].car.displacement);
      this.setFormField('step_1', 'car.casenumber', u.assets[0].car.casenumber);
      this.setFormField('step_1', 'car.plate', u.assets[0].car.plate);
      this.setFormField('step_1', 'car.matriculationYear', parseMatriculationYear(u.assets[0].car.matriculationYear));
      this.setFormField('step_1', 'car.registrationOdometer', u.assets[0].car.registrationOdometer);

      if (u.assets[0].car && u.assets[0].car.vehicleType) {
        this.setFormField('step_1', 'car.vehicleType', u.assets[0].car.vehicleType);
      } else {
        this.setFormField('step_1', 'car.vehicleType', 'car');
      }
    }
  }

  getPositionTitle(position) {
    switch (position || this.position) {
      case 2:
        return this.translationsStore.current['registration_1']['header'];
      case 3:
        return this.translationsStore.current['registration_3']['header'];
      case 4:
        return this.translationsStore.current['registration_4']['header'];
      case 5:
        return this.translationsStore.current['registration_5']['header'];
      case 6:
        return this.translationsStore.current['registration_5']['header_upload'];
      case 7:
        return this.translationsStore.current['registration_6']['header'];
      default:
        return this.translationsStore.current['registration_0']['header'];
    }
  }

  @computed
  get paymentType() {
    if (this.subscription) {
      const ratePlan = this.subscription.zuoraRecurringRatePlan ? 'zuoraRecurringRatePlan' : this.subscription.zuoraOneTimeRatePlan ? 'zuoraOneTimeRatePlan' : null;
      if (ratePlan) {
        try {
          if (this.subscription[ratePlan].paymentType) {
            return this.subscription[ratePlan].paymentType.pageType;
          } else {
            console.error(`no payment type defined for ${ratePlan}`);
          }
        } catch (e) {
          console.error('error getting payment type', e);
        }
      } else {
        return undefined;
      }
    }
  }

  @computed
  get isPaymentExternal() {
    return this.paymentType === 'Other' || this.paymentType === 'Bonifico' || this.paymentType === 'Cash';
  }

  @computed
  get documentName() {
    if (this.user &&
      this.user.assets &&
      this.user.assets[0] &&
      this.user.assets[0].subscription &&
      this.user.assets[0].subscription.zuoraOneTimeRatePlan &&
      this.user.assets[0].subscription.zuoraOneTimeRatePlan.documentType === 'custom') {

      return 'condizioni_generali_vendita_custom_2024.pdf';
    } else if(this.profileStore.profile.condizioniGeneraliFilename) {

      return this.profileStore.profile.condizioniGeneraliFilename;
    } else {

      return 'condizioni_generali_comodato_2022.pdf';
    }
  }
}

export default new RegistrationStore(translationsStore, usersStore, profileStore);



// WEBPACK FOOTER //
// ./src/stores/registrationStore/registrationStore.js